import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import HttpService from "../services/httpService";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";

const useStyles = makeStyles((theme) => ({
  dashboardContainer: {
    padding: theme.spacing(3),
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },
  performanceTable: {
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    marginBottom: theme.spacing(3),
  },
  tableHeader: {
    backgroundColor: "#1C375D",
    "& th": {
      color: "white",
      fontWeight: "bold",
      fontSize: "0.9rem",
      padding: theme.spacing(2),
      borderBottom: "2px solid #dee2e6",
    },
  },
  tableCell: {
    padding: theme.spacing(2),
    borderBottom: "1px solid #dee2e6",
    fontSize: "0.9rem",
  },
  clickableValue: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: "#2c3e50",
    cursor: "pointer",
    textDecoration: "underline",
    "&:hover": {
      color: "#1a73e8",
    },
  },
  clickableNPS: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: "#2c3e50",
    cursor: "pointer",
    "&:hover": {
      color: "#1a73e8",
      textDecoration: "underline",
    },
  },
  metricSubtext: {
    fontSize: "0.8rem",
    color: "#666",
    marginTop: theme.spacing(0.5),
  },
  performanceBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "0.75rem",
    fontWeight: "bold",
    marginLeft: theme.spacing(1),
  },
  redCell: {
    backgroundColor: "#ffebee",
    fontWeight: "bold",
    color: "#c62828",
  },
  yellowCell: {
    backgroundColor: "#fffde7", // Light yellow background
    fontWeight: "bold",
    color: "black", // Orange text color for better contrast
  },
  greenCell: {
    backgroundColor: "#e8f5e8",
    fontWeight: "bold",
    color: "#2e7d32",
  },
  goldCell: {
    backgroundColor: "#fff3e0", // Light orange background for gold
    fontWeight: "bold",
    color: "#ff6f00", // Darker orange for gold text
  },
  positiveValue: {
    color: "#27ae60",
    fontWeight: "bold",
    cursor: "pointer",
    "&:hover": {
      color: "#1e8449",
      textDecoration: "underline",
    },
  },
  negativeValue: {
    color: "#e74c3c",
    fontWeight: "bold",
    cursor: "pointer",
    "&:hover": {
      color: "#b03a2e",
      textDecoration: "underline",
    },
  },
  netValue: {
    color: "#2c3e50",
    fontWeight: "bold",
    cursor: "pointer",
    "&:hover": {
      color: "#1a73e8",
      textDecoration: "underline",
    },
  },
  dialogTable: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  dialogTableHeader: {
    backgroundColor: "#1C375D",
    "& th": {
      color: "white",
      fontWeight: "bold",
      fontSize: "0.9rem",
      padding: theme.spacing(1.5),
      borderRight: "1px solid #dee2e6",
      "&:last-child": {
        borderRight: "none",
      },
    },
  },
  dialogTableCell: {
    padding: theme.spacing(1.5),
    borderBottom: "1px solid #e0e0e0",
    borderRight: "1px solid #e0e0e0",
    fontSize: "0.8rem",
    "&:last-child": {
      borderRight: "none",
    },
  },
  downloadButton: {
    backgroundColor: "#27ae60",
    color: "white",
    fontWeight: "bold",
    padding: "8px 20px",
    "&:hover": {
      backgroundColor: "#219653",
    },
    "&:disabled": {
      backgroundColor: "#95a5a6",
      color: "#7f8c8d",
    },
  },
  nonClickableValue: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: "#2c3e50",
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
  },
  // New styles for dialog header
  dialogHeader: {
    backgroundColor: "#1C375D",
    color: "white",
    padding: theme.spacing(2),
  },
  dialogTitle: {
    color: "white",
    fontWeight: "bold",
    fontSize: "1.25rem",
  },
  dialogSubtitle: {
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "0.9rem",
  },
  closeButton: {
    color: "white",
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#f8f9fa",
  },
  linkStyle: {
    textDecoration: 'underline',
    color: '#1C375D',
    fontWeight: 700,
    '&:hover': {
      color: '#152642',
    },
  },
  // New styles for net impact sub values
  netImpactSubValue: {
    fontSize: '0.75rem',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  netImpactContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  netImpactRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
}));

function PerformanceDashboardDesignTeam(props) {
  const classes = useStyles();
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentData, setCurrentData] = useState(null);
  const [currentTitle, setCurrentTitle] = useState("");
  const [dialogLoading, setDialogLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  const fetchPerformanceData = async (fromDate = null, toDate = null) => {
    try {
      setLoading(true);
      const data = {};
      if (fromDate) data.startDate = fromDate;
      if (toDate) data.endDate = toDate;
      const response = await HttpService.getDesignerPerformance(data);
      setPerformanceData(response?.data?.data);
    } catch (error) {
      console.error("Error fetching performance data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      alert("Start date cannot be after end date");
      return;
    }
    fetchPerformanceData(startDate, endDate);
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    fetchPerformanceData();
  };

  const formatCurrency = (amount) => {
    return `₹${(amount / 100000).toFixed(2)}L`;
  };

  const getTargetAchievementColor = (percentage) => {
    if (percentage > 100) return "#ff9800"; // Orange for gold
    if (percentage >= 81) return "#4caf50"; // Green
    if (percentage >= 51) return "#ffeb3b"; // Bright yellow
    return "#f44336"; // Red
  };

  const getTargetAchievementCategory = (percentage) => {
    if (percentage > 100) return "Gold";
    if (percentage >= 81) return "Green";
    if (percentage >= 51) return "Yellow";
    return "Red";
  };

  const getPerformanceBadge = (percentage) => {
    const category = getTargetAchievementCategory(percentage);
    const color = getTargetAchievementColor(percentage);
    return (
      <span
        className={classes.performanceBadge}
        style={{
          backgroundColor: color,
          color: category === "Yellow" ? "#333" : "white"
        }}
      >
        {category}
      </span>
    );
  };

  const normalizeLeadIds = (ids = []) => {
    if (!ids) return [];
    const list = Array.isArray(ids) ? ids : [ids];
    return list
      .map((id) => {
        if (id && typeof id === "object") {
          return id._id || id.id || id.leadId || id.lead_id || id.lead || null;
        }
        return id;
      })
      .filter((id) => {
        return id !== null && id !== undefined && id !== "" && id !== "null" && id !== "undefined";
      });
  };

  const handleValueClick = (metric, leadIds, title) => {
    const normalizedLeadIds = leadIds && leadIds.length > 0 
      ? leadIds 
      : normalizeLeadIds(metric.leadIds || []);
    if (normalizedLeadIds && normalizedLeadIds.length > 0) {
      fetchLeadDetails(normalizedLeadIds, title);
    } else {
      showMetricDetails(metric, title);
    }
  };

  const fetchLeadDetails = async (leadIds, title) => {
    try {
      setDialogLoading(true);
      const response = await HttpService.getLeadDesignDetails(leadIds);
      const leadsData = response?.data?.data || [];
      let formattedData = [];

      // Create Link components for Project No. and Lead No. using only IDs
      const createProjectLink = (lead) => (
        <Link
          to={`/project-logs/${lead._id}`}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.linkStyle}
        >
          {lead.projectNo || 'N/A'}
        </Link>
      );

      const createLeadLink = (lead) => (
        <Link
          to={`/project-logs/${lead._id}`}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.linkStyle}
        >
          {lead.leadNo || 'N/A'}
        </Link>
      );

      if (title.includes('NPS')) {
        formattedData = leadsData.map(lead => ({
          projectNo: createProjectLink(lead),
          leadNo: createLeadLink(lead),
          clientName: lead.clientName || 'N/A',
          currentStage: lead.currentStage || 'N/A',
          value: lead.npsScore || 'N/A',
          feedback: lead.feedback || 'No feedback available',
          designUser: lead.designUser || 'N/A',
          surveyDate: lead.designSignOffDate ? new Date(lead.designSignOffDate).toLocaleDateString() : 'N/A'
        }));
      } else if (title.includes('Impact')) {
        formattedData = leadsData.map(lead => ({
          projectNo: createProjectLink(lead),
          leadNo: createLeadLink(lead),
          clientName: lead.clientName || 'N/A',
          currentStage: lead.currentStage || 'N/A',
          value: lead.revisedValue || 'N/A',
          originalBudget: lead.originalBudget || 'N/A',
          netImpact: lead.netImpact ? `₹${Math.abs(lead.netImpact).toLocaleString('en-IN')}` : '₹0',
          impactType: lead.impactType || 'N/A',
          designStage: lead.designStage || 'N/A'
        }));
      } else if (title.includes('Revenue')) {
        formattedData = leadsData.map(lead => ({
          projectNo: createProjectLink(lead),
          leadNo: createLeadLink(lead),
          clientName: lead.clientName || 'N/A',
          currentStage: lead.currentStage || 'N/A',
          value: lead.value || 'N/A',
          contractSignedValue: lead.contractSignedValue || 'N/A',
          finalAmount: lead.finalAmountInClosure || 'N/A',
          paymentDone: lead.paymentDone || 'N/A'
        }));
      } else if (title.includes('Lost')) {
        formattedData = leadsData.map(lead => ({
          projectNo: createProjectLink(lead),
          leadNo: createLeadLink(lead),
          clientName: lead.clientName || 'N/A',
          currentStage: lead.currentStage || 'N/A',
          value: lead.estimatedBudget || '₹0',
          lostDate: lead.lostDate ? new Date(lead.lostDate).toLocaleDateString() : 'N/A',
          reasonForLost: lead.reasonForLost || 'No reason provided',
          salesUser: lead.salesUser || 'N/A'
        }));
      } else if (title.includes('PV Upgrade') || title.includes('PV Dedrage')) {
        formattedData = leadsData.map(lead => ({
          projectNo: createProjectLink(lead),
          leadNo: createLeadLink(lead),
          clientName: lead.clientName || 'N/A',
          currentStage: lead.currentStage || 'N/A',
          value: lead.value || 'N/A',
          originalBudget: lead.originalBudget || 'N/A',
          netImpact: lead.netImpact ? `₹${Math.abs(lead.netImpact).toLocaleString('en-IN')}` : '₹0',
          impactType: lead.impactType || 'N/A',
          projectType: title.includes('Upgrade') ? 'Upgrade' : 'Dedrage'
        }));
      } else if (title.includes('Positive Impact')) {
        formattedData = leadsData.map(lead => ({
          projectNo: createProjectLink(lead),
          leadNo: createLeadLink(lead),
          clientName: lead.clientName || 'N/A',
          currentStage: lead.currentStage || 'N/A',
          originalBudget: lead.originalBudget || 'N/A',
          revisedValue: lead.revisedValue || 'N/A',
          netImpact: lead.netImpact ? `₹${Math.abs(lead.netImpact).toLocaleString('en-IN')}` : '₹0',
          impactType: 'Positive',
          designStage: lead.designStage || 'N/A'
        }));
      } else if (title.includes('Negative Impact')) {
        formattedData = leadsData.map(lead => ({
          projectNo: createProjectLink(lead),
          leadNo: createLeadLink(lead),
          clientName: lead.clientName || 'N/A',
          currentStage: lead.currentStage || 'N/A',
          originalBudget: lead.originalBudget || 'N/A',
          revisedValue: lead.revisedValue || 'N/A',
          netImpact: lead.netImpact ? `₹${Math.abs(lead.netImpact).toLocaleString('en-IN')}` : '₹0',
          impactType: 'Negative',
          designStage: lead.designStage || 'N/A'
        }));
      } else {
        formattedData = leadsData.map(lead => ({
          projectNo: createProjectLink(lead),
          leadNo: createLeadLink(lead),
          clientName: lead.clientName || 'N/A',
          currentStage: lead.currentStage || 'N/A',
          value: lead.value || 'N/A',
          designStage: lead.designStage || 'N/A',
          salesStage: lead.salesStage || 'N/A',
          executionStage: lead.executionStage || 'N/A',
          closureDate: lead.closureDate ? new Date(lead.closureDate).toLocaleDateString() : 'N/A'
        }));
      }

      setCurrentData(formattedData);
      setCurrentTitle(title);
      setDialogOpen(true);

    } catch (error) {
      console.error("Error fetching lead details:", error);
      console.error("Error response:", error.response?.data);
      showMetricDetails({ value: '' }, title);
    } finally {
      setDialogLoading(false);
    }
  };

  const showMetricDetails = (metric, title) => {
    let mockData = [];

    // Create Link components for mock data using only IDs
    const createMockProjectLink = (projectNo, id) => (
      <Link
        to={`/project-logs/${id}`}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.linkStyle}
      >
        {projectNo}
      </Link>
    );

    const createMockLeadLink = (leadNo, id) => (
      <Link
        to={`/project-logs/${id}`}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.linkStyle}
      >
        {leadNo}
      </Link>
    );

    if (title.includes("NPS")) {
      mockData = [
        {
          projectNo: createMockProjectLink("PROJ1001", "PROJ1001"),
          leadNo: createMockLeadLink("L001", "PROJ1001"),
          clientName: "Client A",
          currentStage: "Completed",
          value: metric.value?.toString().split('.')[0] || "8",
          feedback: "Excellent design consultation",
          surveyDate: "2024-01-15"
        }
      ];
    } else if (title.includes("Impact")) {
      mockData = [
        {
          projectNo: createMockProjectLink("PROJ1004", "PROJ1004"),
          leadNo: createMockLeadLink("L004", "PROJ1004"),
          clientName: "Client D",
          currentStage: "Completed",
          value: "₹7,50,000",
          originalBudget: "₹5,00,000",
          netImpact: "+₹2,50,000",
          impactType: "Positive Upgrade"
        }
      ];
    } else if (title.includes("PV Upgrade") || title.includes("PV Dedrage")) {
      mockData = [
        {
          projectNo: createMockProjectLink("PROJ1007", "PROJ1007"),
          leadNo: createMockLeadLink("L007", "PROJ1007"),
          clientName: "Client G",
          currentStage: "Design",
          value: "₹15,00,000",
          originalBudget: title.includes("Upgrade") ? "₹10,00,000" : "₹20,00,000",
          netImpact: title.includes("Upgrade") ? "+₹5,00,000" : "-₹5,00,000",
          projectType: title.includes("Upgrade") ? "Upgrade" : "Dedrage"
        }
      ];
    } else if (title.includes("Positive Impact")) {
      mockData = [
        {
          projectNo: createMockProjectLink("PROJ1008", "PROJ1008"),
          leadNo: createMockLeadLink("L008", "PROJ1008"),
          clientName: "Client H",
          currentStage: "Completed",
          originalBudget: "₹8,00,000",
          revisedValue: "₹10,00,000",
          netImpact: "+₹2,00,000",
          impactType: "Positive"
        }
      ];
    } else if (title.includes("Negative Impact")) {
      mockData = [
        {
          projectNo: createMockProjectLink("PROJ1009", "PROJ1009"),
          leadNo: createMockLeadLink("L009", "PROJ1009"),
          clientName: "Client I",
          currentStage: "Completed",
          originalBudget: "₹12,00,000",
          revisedValue: "₹10,00,000",
          netImpact: "-₹2,00,000",
          impactType: "Negative"
        }
      ];
    } else {
      mockData = [
        {
          projectNo: createMockProjectLink("PROJ1006", "PROJ1006"),
          leadNo: createMockLeadLink("L006", "PROJ1006"),
          clientName: "Client F",
          currentStage: "Completed",
          value: "₹12,00,000",
          completionDate: "2024-01-10",
          status: "Successfully Closed"
        }
      ];
    }

    setCurrentData(mockData);
    setCurrentTitle(title);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentData(null);
    setCurrentTitle("");
  };

  const downloadCSV = () => {
    if (!currentData || currentData.length === 0) return;

    // Extract text content from React elements for CSV
    const extractTextFromElement = (element) => {
      if (typeof element === 'string') return element;
      if (React.isValidElement(element)) {
        // For Link components, get the text content
        return element.props.children || '';
      }
      return String(element);
    };

    const headers = Object.keys(currentData[0]);
    const csvContent = [
      headers.join(","),
      ...currentData.map(row =>
        headers.map(header => {
          const value = row[header] || '';
          const textValue = extractTextFromElement(value);
          return `"${textValue.toString().replace(/"/g, '""')}"`;
        }).join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${currentTitle.replace(/\s+/g, "_")}_details.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getDialogHeaders = () => {
    if (!currentData || currentData.length === 0) return [];

    const headers = Object.keys(currentData[0]);
    return headers.map(header =>
      header.replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
    );
  };

  const renderClickableValue = (metric, index) => {
    const normalizedLeadIds = normalizeLeadIds(metric.leadIds || []);
    // Handle Net Impact with Positive/Negative sub-values
    if (metric.showSubValues && metric.clickable) {
      const positiveLeadIds = normalizeLeadIds(metric.positiveLeadIds || []);
      const negativeLeadIds = normalizeLeadIds(metric.negativeLeadIds || []);
      const combinedLeadIds = Array.from(new Set([
        ...normalizeLeadIds(metric.leadIds || []),
        ...positiveLeadIds,
        ...negativeLeadIds
      ]));

      return (
        <Box key={index} className={classes.netImpactContainer}>
          <Tooltip title={`Click to view ${combinedLeadIds.length} net impact projects`}>
            <Typography
              className={`${classes.clickableValue} ${metric.className || ''}`}
              onClick={() => {
                handleValueClick(
                  { ...metric, leadIds: combinedLeadIds },
                  combinedLeadIds,
                  "Net Impact"
                );
              }}
            >
              {metric.value}
            </Typography>
          </Tooltip>
          <Box className={classes.netImpactRow}>
            <Tooltip title={`Click to view ${positiveLeadIds.length} positive impact projects`}>
              <Typography
                className={`${classes.positiveValue} ${classes.netImpactSubValue}`}
                onClick={() => {
                  handleValueClick(
                    { ...metric, leadIds: positiveLeadIds },
                    positiveLeadIds,
                    "Positive Impact"
                  );
                }}
              >
                {metric.positive}
              </Typography>
            </Tooltip>
            <Typography className={classes.netImpactSubValue} style={{ color: '#666' }}>|</Typography>
            <Tooltip title={`Click to view ${negativeLeadIds.length} negative impact projects`}>
              <Typography
                className={`${classes.negativeValue} ${classes.netImpactSubValue}`}
                onClick={() => {
                  handleValueClick(
                    { ...metric, leadIds: negativeLeadIds },
                    negativeLeadIds,
                    "Negative Impact"
                  );
                }}
              >
                {metric.negative}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      );
    }
    
    // Handle combined PV Upgrade/Dedrage Projects
    if (metric.combined && metric.clickable) {
      const upgradeLeadIds = normalizeLeadIds(metric.upgradeLeadIds || []);
      const degradeLeadIds = normalizeLeadIds(metric.degradeLeadIds || []);

      return (
        <Box key={index}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <Tooltip title={`Click to view ${upgradeLeadIds.length} upgrade projects`}>
              <Typography
                className={classes.clickableValue}
                onClick={() => {
                  handleValueClick(
                    { ...metric, leadIds: upgradeLeadIds },
                    upgradeLeadIds,
                    "PV Upgrade Projects"
                  );
                }}
                style={{ marginRight: '8px' }}
              >
                {metric.value.split('/')[0].trim()}
              </Typography>
            </Tooltip>
            <Typography style={{ margin: '0 4px' }}>/</Typography>
            <Tooltip title={`Click to view ${degradeLeadIds.length} degrade projects`}>
              <Typography
                className={classes.clickableValue}
                onClick={() => {
                  handleValueClick(
                    { ...metric, leadIds: degradeLeadIds },
                    degradeLeadIds,
                    "PV Dedrage Projects"
                  );
                }}
                style={{ marginLeft: '8px' }}
              >
                {metric.value.split('/')[1].trim()}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      );
    }

    // Handle regular metrics - always pass normalizedLeadIds even if empty
    if (metric.clickable) {
      return (
        <Tooltip 
          title={normalizedLeadIds.length > 0 
            ? `Click to view ${normalizedLeadIds.length} projects` 
            : "Click to view details"
          } 
          key={index}
        >
          <Typography
            className={`${classes.clickableValue} ${metric.className || ''}`}
            onClick={() => {
              handleValueClick(metric, normalizedLeadIds, metric.label);
            }}
          >
            {metric.value}
          </Typography>
        </Tooltip>
      );
    }

    return (
      <Typography className={classes.nonClickableValue} key={index}>
        {metric.value}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box className={classes.dashboardContainer}>
        <Typography variant="h4" align="center">
          Loading Performance Dashboard...
        </Typography>
      </Box>
    );
  }

  if (!performanceData) {
    return (
      <Box className={classes.dashboardContainer}>
        <Typography variant="h4" align="center">
          No performance data available
        </Typography>
      </Box>
    );
  }

  const { performanceMetrics } = performanceData;
  const currentPerformance = performanceMetrics.targetAchievement?.percentage || 0;

  // First Table Data
  const firstTableData = [
    {
      label: "Joining Date",
      value: new Date(performanceMetrics.joiningDate).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }),
      subtext: "Career Start Date",
      leadIds: [],
      clickable: false,
    },
    {
      label: "Count of Closed Projects",
      value: performanceMetrics.closedProjects?.count || 0,
      subtext: "Successfully Completed",
      leadIds: performanceMetrics.closedProjects?.leadIds || [],
      clickable: true,
    },
    {
      label: "Total Revenue",
      value: formatCurrency(performanceMetrics.totalRevenue?.value || 0),
      subtext: `${performanceMetrics.totalRevenue?.leadIds?.length || 0} Projects`,
      leadIds: performanceMetrics.totalRevenue?.leadIds || [],
      clickable: true,
    },
    {
      label: "PV Upgrade Projects / PV Dedrage Projects",
      value: `${performanceMetrics.pvUpgradeProjects?.count || 0} / ${performanceMetrics.pvDegradeProjects?.count || 0}`,
      subtext: "Value Increased / Value Decreased",
      upgradeLeadIds: performanceMetrics.pvUpgradeProjects?.leadIds || [],
      degradeLeadIds: performanceMetrics.pvDegradeProjects?.leadIds || [],
      clickable: true,
      combined: true,
    }
  ];

  // Net Impact Data - Now shows Positive and Negative as sub-values
  const netImpactData = [
    {
      label: "Net Impact",
      value: formatCurrency(performanceMetrics.netImpact?.net || 0),
      positive: `+${formatCurrency(performanceMetrics.netImpact?.positive || 0)}`,
      negative: `-${formatCurrency(Math.abs(performanceMetrics.netImpact?.negative || 0))}`,
      subtext: "Overall Impact",
      className: classes.netValue,
      leadIds: performanceMetrics.netImpact?.leadIds || [],
      positiveLeadIds: performanceMetrics.netImpact?.positiveLeadIds || [],
      negativeLeadIds: performanceMetrics.netImpact?.negativeLeadIds || [],
      clickable: true,
      showSubValues: true,
    }
  ];

  // Second Table Data
  const secondTableData = [
    {
      label: "NPS-Design",
      value: performanceMetrics.npsDesign?.value?.toFixed(1) || "0.0",
      subtext: `${performanceMetrics.npsDesign?.totalSurveys || 0} Surveys`,
      leadIds: performanceMetrics.npsDesign?.leadIds || [],
      clickable: true,
      className: classes.clickableNPS,
    },
    {
      label: "NPS-Post Design",
      value: performanceMetrics.npsPostDesign?.value?.toFixed(1) || "0.0",
      subtext: `${performanceMetrics.npsPostDesign?.totalSurveys || 0} Surveys`,
      leadIds: performanceMetrics.npsPostDesign?.leadIds || [],
      clickable: true,
      className: classes.clickableNPS,
    },
    {
      label: "Target Achievement",
      value: `${performanceMetrics.targetAchievement?.percentage || 0}%`,
      subtext: `Target: ${formatCurrency(performanceMetrics.targetAchievement?.targetAmount || 0)}`,
      badge: getPerformanceBadge(performanceMetrics.targetAchievement?.percentage || 0),
      leadIds: performanceMetrics.targetAchievement?.leadIds || [],
      clickable: true,
    },
    {
      label: "Lost Projects",
      value: performanceMetrics.lostProjects?.count || 0,
      subtext: "Unsuccessful Projects",
      leadIds: performanceMetrics.lostProjects?.leadIds || [],
      clickable: true,
    }
  ];

  // Category Performance Data
  const categoryData = [
    {
      category: "Category Performance",
      red: "Red",
      yellow: "Yellow",
      green: "Green",
      gold: "Gold"
    },
    {
      category: "Parameters",
      red: "<=50%",
      yellow: "51%-80%",
      green: "81%-100%",
      gold: ">100%"
    },
    {
      category: "Actions",
      red: "Less than 50% could lead to Performance Improve Plan",
      yellow: "Please improve performance to help avoid concerns.",
      green: "Kudos, Ready for Rewards",
      gold: "Rewards and Growth"
    }
  ];

  return (
    <div className={classes.dashboardContainer}>
      {/* Header */}
      <div mb={4}>
        <Typography
          variant="h4"
          style={{
            margin: "10px 5px",
            textAlign: "center",
            fontWeight: "bold",
            textDecoration: 'underline'
          }}
        >
          My Performance Dashboard
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" style={{ textAlign: "center", fontWeight: "bold", margin: "15px 0px" }}>
          Performance snapshot of your whole journey with Decorpot - Click on any value to view details
        </Typography>
      </div>

      {/* Date Filter Section */}
      <Paper style={{ padding: "20px", marginBottom: "20px" }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              id="start-date"
              label="From Date"
              type="date"
              variant="outlined"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              id="end-date"
              label="To Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDateFilter}
                disabled={!startDate || !endDate}
              >
                Submit
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearFilter}
                disabled={!startDate && !endDate}
                style={{marginLeft:"20px"}}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* First Table - Now with only 5 columns */}
      <TableContainer component={Paper} className={classes.performanceTable}>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>Joining Date</TableCell>
              <TableCell align="center">Count of Closed Projects</TableCell>
              <TableCell align="center">Total Revenue</TableCell>
              <TableCell align="center">Value Upgrade Projects / Value Dedrage Projects</TableCell>
              <TableCell align="center">Net Impact</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {firstTableData.map((metric, index) => (
                <TableCell key={index} className={classes.tableCell} align="center" component='th'>
                  {renderClickableValue(metric, index)}
                  <Typography className={classes.metricSubtext}>
                    {metric.subtext}
                  </Typography>
                </TableCell>
              ))}
              {netImpactData.map((metric, index) => (
                <TableCell key={`net-${index}`} className={classes.tableCell} align="center" component='th'>
                  {renderClickableValue(metric, index)}
                  <Typography className={classes.metricSubtext}>
                    {metric.subtext}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Second Table */}
      <TableContainer component={Paper} className={classes.performanceTable}>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>NPS-Design</TableCell>
              <TableCell align="center">NPS-Post Handover</TableCell>
              <TableCell align="center">Target Achievement</TableCell>
              <TableCell align="center">Lost Projects</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {secondTableData.map((metric, index) => (
                <TableCell key={index} className={classes.tableCell} align="center" component='th'>
                  <Box>
                    {renderClickableValue(metric, index)}
                    {metric.badge}
                  </Box>
                  <Typography className={classes.metricSubtext}>
                    {metric.subtext}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Category Performance Table */}
      <TableContainer component={Paper} className={classes.performanceTable}>
        <Table>
          <TableBody>
            {categoryData.map((row, index) => (
              <TableRow key={index}>
                <TableCell className={classes.tableHeader} component='th'>
                  <Typography style={{ fontWeight: "bold", color: "white" }}>
                    {row.category}
                  </Typography>
                </TableCell>
                <TableCell component='th' className={`${classes.tableCell} ${classes.redCell}`} style={{ color: "black", background: "red" }} align="center">
                  {row.red}
                </TableCell>
                <TableCell component='th' className={`${classes.tableCell} ${classes.yellowCell}`} style={{ color: "black", background: "yellow" }} align="center">
                  {row.yellow}
                </TableCell>
                <TableCell component='th'className={`${classes.tableCell} ${classes.greenCell}`} style={{ color: "black", background: "green" }} align="center">
                  {row.green}
                </TableCell>
                <TableCell component='th' className={`${classes.tableCell} ${classes.goldCell}`} style={{ color: "black", background: "orange" }} align="center">
                  {row.gold}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog with Colored Header and Single Download Button */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        {/* Colored Dialog Header */}
        <Box className={classes.dialogHeader}>
          <DialogTitle style={{ padding: 0 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography className={classes.dialogTitle}>
                  {currentTitle} - Project Details
                </Typography>
                <Typography className={classes.dialogSubtitle}>
                  {currentData?.length || 0} projects found
                </Typography>
              </Box>
              <IconButton
                onClick={handleCloseDialog}
                className={classes.closeButton}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
        </Box>

        <DialogContent style={{ padding: 0 }}>
          {dialogLoading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
              <Typography style={{ marginLeft: '16px' }}>
                Loading project details...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} className={classes.dialogTable} style={{ margin: 16 }}>
                <Table>
                  <TableHead className={classes.dialogTableHeader}>
                    <TableRow>
                      {getDialogHeaders().map((header, index) => (
                        <TableCell key={index} align="center">
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentData && currentData.map((row, index) => (
                      <TableRow key={index} hover>
                        {Object.values(row).map((value, cellIndex) => (
                          <TableCell key={cellIndex} className={classes.dialogTableCell} align="center">
                            {value}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {(!currentData || currentData.length === 0) && (
                <Typography variant="body1" align="center" style={{ padding: '40px' }}>
                  No project data available
                </Typography>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={downloadCSV}
            className={classes.downloadButton}
            disabled={!currentData || currentData.length === 0}
            size="large"
          >
            Download CSV ({currentData?.length || 0} records)
          </Button>
          <Button onClick={handleCloseDialog} color="primary" variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default connect(null, {})(PerformanceDashboardDesignTeam);
